import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, X, Share2, Plus } from './Icon';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'gromly_install_dismissed';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;
}

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOS, setShowIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    if (isIOS()) {
      setShowIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  }

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50
      bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl p-4 flex gap-3 items-start
      animate-in slide-in-from-bottom-4 duration-300">
      <img src="/pwa-192.png" alt="Gromly" className="w-10 h-10 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{t('installPrompt.title')}</p>
        {showIOS ? (
          <div className="mt-1.5">
            <p className="text-xs text-slate-400">{t('installPrompt.iosIntro')}</p>
            <ol className="mt-2 space-y-1.5">
              <li className="flex items-center gap-2 text-xs text-slate-300">
                <span className="w-4 h-4 rounded-full bg-slate-700 text-slate-200 text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                <span>{t('installPrompt.iosStep1')}</span>
                <Icon as={Share2} size={14} className="text-sky-400 shrink-0" />
              </li>
              <li className="flex items-center gap-2 text-xs text-slate-300">
                <span className="w-4 h-4 rounded-full bg-slate-700 text-slate-200 text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                <span>{t('installPrompt.iosStep2')}</span>
                <Icon as={Plus} size={14} className="text-teal-400 shrink-0" />
              </li>
            </ol>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-400 mt-0.5">{t('installPrompt.subtitle')}</p>
            <button
              onClick={install}
              className="mt-2 flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-400 transition-colors px-3 py-1.5 rounded-lg cursor-pointer"
            >
              <Icon as={Plus} size={12} />
              {t('installPrompt.install')}
            </button>
          </>
        )}
      </div>
      <button onClick={dismiss} aria-label={t('common.close')} className="text-slate-500 hover:text-slate-300 transition-colors cursor-pointer shrink-0">
        <Icon as={X} size={16} />
      </button>
    </div>
  );
}
